'use client';

import { useRef, useEffect } from 'react';
import gsap from 'gsap';

interface CounterProps {
  start?: number;
  end?: number;
  duration?: number;
  className?: string;
}

export const Counter: React.FC<CounterProps> = ({
  start = 0,
  end = 100,
  duration = 2,
  className,
}) => {
  const counterRef = useRef<HTMLSpanElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (counterRef.current) {
            gsap.fromTo(
              counterRef.current,
              { innerHTML: start },
              {
                innerHTML: end,
                duration: duration,
                ease: 'power1.inOut',
                snap: { innerHTML: 1 },
                onUpdate: function () {
                  if (counterRef.current) {
                    counterRef.current.innerHTML =
                      Math.ceil(
                        Number(counterRef.current.innerHTML),
                      ).toString() + '%';
                  }
                },
              },
            );
          }
          if (observerRef.current) {
            observerRef.current.disconnect(); // Stop observing after animation starts
          }
        }
      });
    };

    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold: 0.1,
    });

    if (counterRef.current) {
      observerRef.current.observe(counterRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [start, end, duration]);

  return (
    <span className={className} ref={counterRef}>
      {start}
    </span>
  );
};
